import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import { fetchCurrentPromotionTerms } from '../../infra/api/fetchCurrentPromotion';
import { useSnackStack } from '../../ui/component/StackingSnackbar/StackingSnackbarProvider';
import { useBaseParam } from '../hooks/useBaseParam';
import useBeacon from '../hooks/useBeacon';
import usePromoBloc from '../hooks/usePromoBloc';
import { useReuseBottomSheet } from '../hooks/useReuseBottomSheet';
import { useTermCheck } from '../hooks/useTermCheck';
import { useTermFullsheet } from '../hooks/useTermFullsheet';
import { useTerms } from '../hooks/useTerms';
import { beaconSelector } from '../recoil/selector';
import { TERM_CREDIT_LOAN, TERM_MTG_LOAN } from '../staticData/terms';
import { EVENT_PAGE, PAGE_NM } from '../types/types';
import { mutatePromotionEntry } from '../../infra/api/mutatePromotionEntry';

export const useTerm = () => {
  const location: any = useLocation();
  const state = location.state;

  const beaconData = useRecoilValue(beaconSelector);
  const beacon = useBeacon({
    mediaCd: beaconData.mediaCd,
    custSeq: beaconData.custSeq,
    srcCd: beaconData.srcCd,
    pageName: state?.EVENT_STATE === 'mtg' ? PAGE_NM.MTG_TERMS : PAGE_NM.TERMS,
  });
  const sendBeaconClkBtnAgree = (btnNm: string) => {
    beacon.sendClkBtnAgree({
      properties: { btnNm },
    });
  };
  const sendBeaconClkBtnInputResume = (btnNm: string) => {
    beacon.sendClkBtnInputResume({
      properties: { btnNm },
    });
  };
  const sendBeaconClkBtnInputBegin = (btnNm: string) => {
    beacon.sendClkBtnInputBegin({
      properties: { btnNm },
    });
  };
  useEffect(() => {
    beacon.sendViewPage();
  }, []);

  const [isMtg, setMtg] = useState<boolean>(false);
  const [currentPromotionTerms, setCurrentPromotionTerms] = useState<
    any | undefined
  >(undefined);
  const [currentProtionSeq, setCurrentPromotionSeq] = useState<
    number | undefined
  >(undefined);

  const getCurrentPromotionTerms = useBaseParam(fetchCurrentPromotionTerms, {
    mediaCd: beaconData.mediaCd,
    custSeq: beaconData.custSeq,
    loanType: state?.EVENT_STATE === 'mtg' ? 'HOUSE_LOAN' : 'CREDIT_LOAN',
  });

  const getPromotionTerm = async (mtg: boolean) => {
    const res = await getCurrentPromotionTerms();
    if (res.data.isSucceed && res.data.result) {
      res.data.result.forEach((item: any) => (item.checked = true));
      const terms = res.data.result.filter((item: any) => item.agreeYn !== 'Y');
      setCurrentPromotionTerms(terms);
      if (terms.length > 0) {
        setCurrentPromotionSeq(res.data.result[0].promotionSeq);
      }
    }
  };

  const { addToast } = useSnackStack();
  const onClickPromotionEvent = (term: any) => {
    if (term.requiredYn === 'Y') {
      addToast({ message: '필수 선택항목입니다.' });
      return;
    }
    const newPromotionTerms = currentPromotionTerms.map((item: any) => {
      if (item.promotionTermsSeq == term.promotionTermsSeq) {
        term.checked = !term.checked;
        return term;
      } else {
        return item;
      }
    });
    setCurrentPromotionTerms(newPromotionTerms);
  };

  useEffect(() => {
    const mtg = state?.EVENT_STATE === 'mtg';
    setMtg(mtg);
    getPromotionTerm(mtg);
  }, [state]);

  const termCheck = useTermCheck(
    isMtg ? TERM_MTG_LOAN : TERM_CREDIT_LOAN,
    'essential',
  );

  // todo 약관 조회 api test
  // const test = useBaseParam(fetchTerms, { termsType: '1020' });
  // useEffect(() => {
  //   (async () => {
  //     const res = await test();
  //     console.log(res);
  //   })();
  // }, []);

  const termFullsheet = useTermFullsheet();

  const { putPromotionEntry } = usePromoBloc(state?.EVENT_OPTION);
  const getEventExecute = () => {
    if (state?.EVENT_PAGE === EVENT_PAGE.PROMOTION) {
      return putPromotionEntry();
    }
    return;
  };

  const putPromotionEntryByCurrentPromotion = useBaseParam(
    mutatePromotionEntry,
    {
      custSeq: beaconData.custSeq,
      promotionSeq: currentProtionSeq ?? 0,
      promotionTermsList: currentPromotionTerms?.map((item: any) => {
        return {
          promotionTermsSeq: item.promotionTermsSeq,
          agreeYn: item.checked ? 'Y' : 'N',
        };
      }),
    },
  );

  const savePromotionEntry = async () => {
    if (
      !state?.EVENT_OPTION &&
      currentProtionSeq &&
      currentProtionSeq > 0 &&
      currentPromotionTerms &&
      currentPromotionTerms.length > 0
    ) {
      await putPromotionEntryByCurrentPromotion();
    }
  };

  const terms = useTerms({
    EVENT_REQUEST: state?.EVENT_REQUEST,
    EVENT_EXECUTE: () => getEventExecute(),
    EVENT_STATE: isMtg ? 'mtg' : state?.EVENT_STATE,
    ref_page: isMtg ? PAGE_NM.MTG_HOME : PAGE_NM.HOME,
  });

  const { navigateMtgStep, setReuseInfo } = useReuseBottomSheet();

  return {
    termCheck,
    termFullsheet,
    terms,
    isMtg,
    currentPromotionTerms,

    sendBeaconClkBtnAgree,
    sendBeaconClkBtnInputResume,
    sendBeaconClkBtnInputBegin,

    onClickPromotionEvent,
    savePromotionEntry,

    navigateMtgStep,
    setReuseInfo,
  };
};
